import React from 'react';
import { CloudflareLogo } from '@ui/components';
import { Container } from './styled';
import { ChoiceGroup, DefaultButton, IChoiceGroupOption, MessageBar, MessageBarType } from '@fluentui/react';
import { submitEML, RateLimitError, Disposition } from './functions';
import { Cloudy } from '../Cloudy';


export const SubmissionForm = () => {
  const submissionOptions: IChoiceGroupOption[] = [
    {
      key: 'spam',
      // @ts-ignore
      text: (
        <div>
          <b>Spam</b>
          <br />
          <span style={{ color: 'gray' }}>Unwanted random emails you wish to no longer receive</span>
        </div>
      ),
    },
    {
      key: 'malicious',
      // @ts-ignore
      text: (
        <div>
          <b>Phish</b>
          <br />
          <span style={{ color: 'gray' }}>
            Emails that are suspected to have malicious intent, including hack, scam & fraud
          </span>
        </div>
      ),
    },
    {
      key: 'bulk',
      // @ts-ignore
      text: (
        <div>
          <b>Bulk</b>
          <br />
          <span style={{ color: 'gray' }}>Unwanted marketing emails you wish to no longer receive</span>
        </div>
      ),
    },
  ];

  const [submissionType, setSubmissionType] = React.useState<IChoiceGroupOption>(submissionOptions[0]);
  const [errorMessage, setErrorMessage] = React.useState<string | false>(false);
  const [rateLimited, setRateLimited] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [submissionComplete, setSubmissionComplete] = React.useState(false);

  React.useEffect(() => {
    if (!rateLimited) return;
    const timer = setTimeout(() => setRateLimited(false), 5000);
    return () => clearTimeout(timer);
  }, [rateLimited]);

  const onChangeSubmissionType = (_event: React.FormEvent<HTMLElement>, option: IChoiceGroupOption): void => {
    setSubmissionType(option);
  };

  const handleSubmit = async () => {
    setErrorMessage(false);
    setLoading(true);

    const clientId = new URLSearchParams(window.location.search).get('clientId');
    if (!clientId) {
      setErrorMessage("Missing client ID");
      setLoading(false);
      return;
    }

    const disposition = Disposition.safeParse(submissionType.key);
    if (!disposition.success) {
      setErrorMessage("Invalid submission type");
      setLoading(false);
      return;
    }

    try {
      window.Sentry?.addBreadcrumb({ category: "submission", message: "Submitting EML", level: "info" });

      await submitEML({
        disposition: disposition.data,
        clientId,
      });

      await new Promise((resolve) => setTimeout(resolve, 3000));

      window.Sentry?.addBreadcrumb({ category: "submission", message: "EML submitted successfully", level: "info" });
      setSubmissionComplete(true);
    } catch (e: any) {
      if (e instanceof RateLimitError) {
        setRateLimited(true);
      } else {
        window.Sentry?.addBreadcrumb({ category: "submission", message: "EML submission failed", level: "error" });
        setErrorMessage(e.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Container loading={loading}>
        <Cloudy />
        <ChoiceGroup
          defaultSelectedKey={submissionType.key}
          options={submissionOptions}
          onChange={onChangeSubmissionType}
          label="Select Submission Type:"
          required
          disabled={loading || submissionComplete}
        />
        <DefaultButton disabled={loading || submissionComplete || rateLimited} onClick={handleSubmit}>
          {loading ? 'Loading...' : 'Submit Report'}
        </DefaultButton>
        {rateLimited && (
          <MessageBar messageBarType={MessageBarType.warning}>
            Too many requests. Please wait a moment before trying again.
          </MessageBar>
        )}
        {errorMessage && <MessageBar messageBarType={MessageBarType.error}>Error: {errorMessage}</MessageBar>}
        {submissionComplete && (
          <MessageBar messageBarType={MessageBarType.success}>
            Report Sent: You may select another email or close this panel.
          </MessageBar>
        )}
      </Container>
      <CloudflareLogo
        css={{
          width: '20vw',
          height: '20vw',
          padding: '2vw',
          position: 'absolute',
          bottom: '0px',
        }}
      />
    </>
  );
};
